import dayjs from 'dayjs';
import get from 'lodash/get';
import { useCallback, useContext } from 'react';
import { Link } from 'react-router-dom';
import { CreateFormModal } from '@/modules/admin-dashboard/components/modals/CreateFormModal.tsx';
import { CreateTaskModal } from '@/modules/admin-dashboard/components/modals/CreateTaskModal.tsx';
import { useCurrentUserFormsListQuery } from '@/modules/admin-dashboard/queries/forms.ts';
import { adminDashboardPaths } from '@/modules/admin-dashboard/routes.tsx';
import { Button, InfoCard, ModalProviderContext, Skeleton } from 'ui';
import { PlusIcon } from 'ui/icons';
import { FormResponse } from 'compliance-api';

const FormsSkeleton = () => {
  return (
    <div className="flex flex-col gap-8">
      {Array(5)
        .fill(null)
        .map((_, i) => (
          <div key={i} className="flex justify-between items-center">
            <div className="flex flex-col gap-2.5">
              <Skeleton width={91} height={13} />
              <Skeleton width={162} height={11} />
            </div>

            <Skeleton width={89} height={13} />
          </div>
        ))}
    </div>
  );
};

const Forms = () => {
  const { openModal } = useContext(ModalProviderContext);

  const { data, isLoading } = useCurrentUserFormsListQuery({
    page: 1,
    page_size: 15,
  });

  const forms = get(data, 'pages', [])
    .flatMap(({ data }) => data)
    .slice(0, 6);

  const handleOpenCreateFormModal = useCallback(() => {
    openModal(CreateFormModal);
  }, [openModal]);

  const handleOpenCreateFormTaskModal = useCallback(
    (form: FormResponse) => {
      openModal(CreateTaskModal, {
        defaultForm: {
          label: form.name,
          value: form.id,
        },
      });
    },
    [openModal],
  );
  return (
    <InfoCard
      title="Forms"
      loading={isLoading}
      classes={{ bottomAction: '!py-2' }}
      headerAction={
        isLoading ? (
          <Skeleton width={15} height={15} borderRadius={15} />
        ) : (
          <button onClick={handleOpenCreateFormModal}>
            <img alt="add task" src={PlusIcon} />
          </button>
        )
      }
      bottomAction={
        isLoading ? (
          <Skeleton width={52} height={11} />
        ) : (
          <Button className="!p-0 min-h-8" variant="plain">
            <Link
              to={{ pathname: adminDashboardPaths.static.forms, search: location.search }}
              className="w-full h-full flex items-center justify-center"
            >
              View all
            </Link>
          </Button>
        )
      }
    >
      {isLoading && <FormsSkeleton />}
      {!isLoading && !forms.length && (
        <div className="w-full h-full flex items-center justify-center py-2.5">
          You don't have forms yet
        </div>
      )}
      {!isLoading && !!forms.length && (
        <div className="flex flex-col gap-3">
          {forms.map(form => (
            <div key={form.id} className="flex justify-between w-full items-center">
              <div className="flex flex-col gap-2">
                <Link
                  to={{
                    pathname: adminDashboardPaths.dynamic.form(form.id),
                    search: location.search,
                  }}
                  replace={false}
                  state={{ returnUrl: adminDashboardPaths.static.dashboard }}
                  className="text-sm hover:underline"
                >
                  {form.name}
                </Link>
                <div className="text-[#737373] text-xs">
                  Last updated: {dayjs(form.updated_at).format('DD-MM-YYYY, HH:mm')}
                </div>
              </div>
              <div>
                <Button variant="plain" onClick={handleOpenCreateFormTaskModal.bind(null, form)}>
                  Create a task
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </InfoCard>
  );
};

export default Forms;
